// frontend/src/pages/Settings/Comp/VehiclesTab.jsx
import React, { useMemo, useState } from 'react';
import Classes from '../Settings.module.css';
import { CarFrontIcon } from '../../../../../../utils/Icons';
import { useSelector, useDispatch } from 'react-redux';
import backend from '../../../../../../api';
import { setUserData } from '../../../../../../redux-store/auth/actions';

const PencilIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 20h9" stroke="#F18A3A" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M16.5 3.5l4 4L8 20H4v-4L16.5 3.5z" stroke="#F18A3A" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13a2 2 0 002 2h6a2 2 0 002-2l1-13M9 7V5a2 2 0 012-2h2a2 2 0 012 2v2" stroke="#F18A3A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/** ---------------- helpers ---------------- **/
function makeId(seed) {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `v-${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${seed || ''}`;
}

function normalizeSeed(list) {
  return (list || []).map((v, i) => ({
    id: v.id || v._id || (v.plate ? `plate-${v.plate}-${i}` : makeId(i)),
    make: v.make || '',
    model: v.model || '',
    plate: (v.plate || v.licensePlate || '').toUpperCase(),
  }));
}

/**
 * Reads vehicle limit from Redux user.activeSubscription.plan.vehiclesLimit.
 * Convention: 0 or missing = Unlimited.
 * Returns { limitNum, isUnlimited, planKey }
 */
function getPlanVehicleLimit(user) {
  const planKey = user?.activeSubscription?.plan?.key || user?.subscription || 'free';
  const rawLimit = user?.activeSubscription?.plan?.vehiclesLimit;

  // If your backend defines explicit limits per plan, this will pick them up.
  // 0 or undefined/null => Unlimited.
  const limitNum = typeof rawLimit === 'number' ? rawLimit : 0;
  const isUnlimited = !limitNum || limitNum <= 0;

  return { planKey, limitNum, isUnlimited };
}

export default function VehiclesTab({ initialVehicles, onAdd, onEdit, onDelete }) {
  const { user } = useSelector((s) => s.auth);
  const dispatch = useDispatch();

  const { planKey, limitNum, isUnlimited } = getPlanVehicleLimit(user);

  const seeded = initialVehicles || user?.customerProfile?.vehicle?.vehicles || [];
  const [vehicles, setVehicles] = useState(() => normalizeSeed(seeded));

  // allow per-item editing (multiple rows at once)
  const [editingIds, setEditingIds] = useState(new Set());
  const [drafts, setDrafts] = useState({}); // { [id]: { make, model, plate } }

  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ make: '', model: '', plate: '' });

  const countLabel = useMemo(() => {
    const right = isUnlimited ? '∞' : String(limitNum);
    return `Vehicles (${vehicles.length}/${right})`;
  }, [vehicles.length, isUnlimited, limitNum]);

  const canAddMore = useMemo(() => {
    if (isUnlimited) return true;
    return vehicles.length < limitNum;
  }, [vehicles.length, isUnlimited, limitNum]);

  async function persist(newList) {
    const payload = {
      customerProfile: {
        ...(user.customerProfile || {}),
        vehicle: { vehicles: newList.map(({ make, model, plate }) => ({ make, model, plate })) },
      },
    };
    const { data } = await backend.put(`/api/user/${user._id}`, payload);
    await dispatch(setUserData({ ...user, ...data.data }));
  }

  function startAdd() {
    if (!canAddMore) return;
    setDraft({ make: '', model: '', plate: '' });
    setAdding(true);
  }

  async function saveAdd(e) {
    e.preventDefault();
    if (!draft.make.trim() || !draft.model.trim() || !draft.plate.trim()) return;
    if (!canAddMore) return;

    const newItem = {
      id: makeId(),
      make: draft.make.trim(),
      model: draft.model.trim(),
      plate: draft.plate.trim().toUpperCase(),
    };
    const newList = [...vehicles, newItem];
    setVehicles(newList);
    setAdding(false);
    onAdd?.(newItem, vehicles);
    await persist(newList);
  }

  function cancelAdd() { setAdding(false); }

  function startEdit(v) {
    setEditingIds((s) => new Set(s).add(v.id));
    setDrafts((m) => ({ ...m, [v.id]: { make: v.make, model: v.model, plate: v.plate } }));
  }

  async function saveEdit(e, id) {
    e.preventDefault();
    const d = drafts[id];
    if (!d) return;
    const updated = {
      make: d.make.trim(),
      model: d.model.trim(),
      plate: d.plate.trim().toUpperCase(),
    };
    const newList = vehicles.map((v) => (v.id === id ? { ...v, ...updated } : v));
    setVehicles(newList);
    onEdit?.(id, updated, vehicles);
    setEditingIds((s) => { const ns = new Set(s); ns.delete(id); return ns; });
    setDrafts((m) => { const { [id]: _, ...rest } = m; return rest; });
    await persist(newList);
  }

  function cancelEdit(id) {
    setEditingIds((s) => { const ns = new Set(s); ns.delete(id); return ns; });
    setDrafts((m) => { const { [id]: _, ...rest } = m; return rest; });
  }

  async function remove(id) {
    const newList = vehicles.filter((v) => v.id !== id);
    setVehicles(newList);
    onDelete?.(id, vehicles);
    setEditingIds((s) => { const ns = new Set(s); ns.delete(id); return ns; });
    setDrafts((m) => { const { [id]: _, ...rest } = m; return rest; });
    await persist(newList);
  }

  return (
    <div>
      <h3 className="mb-20">
        {countLabel}
        {/* <span className={Classes.planHint}>({planKey})</span> */}
      </h3>

      <div className={Classes.vehiclesList}>
        {vehicles.map((v) => {
          const isEditing = editingIds.has(v.id);
          const d = drafts[v.id] || { make: v.make, model: v.model, plate: v.plate };
          return (
            <div key={v.id} className={Classes.vehicleRow}>
              <div className={Classes.vehicleLeft}>
                <div className={Classes.vehicleAvatar}><CarFrontIcon fill='white' height='22' /></div>

                {isEditing ? (
                  <form className={Classes.inlineForm} onSubmit={(e) => saveEdit(e, v.id)}>
                    <input
                      className={Classes.inputField}
                      placeholder="Make"
                      value={d.make}
                      onChange={(e) => setDrafts((m) => ({ ...m, [v.id]: { ...d, make: e.target.value } }))}
                      required
                    />
                    <input
                      className={Classes.inputField}
                      placeholder="Model"
                      value={d.model}
                      onChange={(e) => setDrafts((m) => ({ ...m, [v.id]: { ...d, model: e.target.value } }))}
                      required
                    />
                    <input
                      className={Classes.inputField}
                      placeholder="License Plate"
                      value={d.plate}
                      onChange={(e) => setDrafts((m) => ({ ...m, [v.id]: { ...d, plate: e.target.value } }))}
                      required
                    />
                    <div className={Classes.inlineActions}>
                      <button className={Classes.saveBtn} type="submit"><h5>Save</h5></button>
                      <button type="button" className={Classes.linkBtn} onClick={() => cancelEdit(v.id)}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  <div>
                    <div className={Classes.vehicleTitle}>{v.make} {v.model}</div>
                    <div className={Classes.vehicleMeta}>{v.plate}</div>
                  </div>
                )}
              </div>

              {!isEditing && (
                <div className={Classes.vehicleActions}>
                  <button className={Classes.iconBtn} title="Edit" onClick={() => startEdit(v)}>
                    <PencilIcon />
                  </button>
                  <button className={Classes.iconBtn} title="Delete" onClick={() => remove(v.id)}>
                    <TrashIcon />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!adding && (
        <button
          className={`${Classes.addLink} ${!canAddMore ? Classes.addLinkDisabled : ''}`}
          onClick={startAdd}
          disabled={!canAddMore}
          title={canAddMore ? 'Add vehicle' : 'Vehicle limit reached for your plan'}
        >
          <PlusIcon /> <span>Add Additional Vehicle(s)</span>
        </button>
      )}

      {adding && (
        <form className={`${Classes.vehicleRow} ${Classes.addForm}`} onSubmit={saveAdd}>
          <div className={Classes.vehicleLeft}>
            <div className={Classes.vehicleAvatar}><CarFrontIcon fill='white' height='22' /></div>
            <div className={Classes.inlineForm}>
              <input
                className={Classes.inputField}
                placeholder="Make (e.g., Toyota)"
                value={draft.make}
                onChange={(e) => setDraft((p) => ({ ...p, make: e.target.value }))}
                required
              />
              <input
                className={Classes.inputField}
                placeholder="Model (e.g., Tacoma)"
                value={draft.model}
                onChange={(e) => setDraft((p) => ({ ...p, model: e.target.value }))}
                required
              />
              <input
                className={Classes.inputField}
                placeholder="License Plate"
                value={draft.plate}
                onChange={(e) => setDraft((p) => ({ ...p, plate: e.target.value }))}
                required
              />
              <div className={Classes.inlineActions}>
                <button className={Classes.saveBtn} type="submit" disabled={!canAddMore}><h5>Save Vehicle</h5></button>
                <button type="button" className={Classes.linkBtn} onClick={() => setAdding(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </form>
      )}

      {!canAddMore && (
        <div className={Classes.limitNote}>
          You’ve reached the maximum of {isUnlimited ? '∞' : limitNum} vehicles for your {planKey} plan.
        </div>
      )}
    </div>
  );
}
